import axios from 'axios';

// Determine the correct API base URL for both localhost and production
const getDefaultBaseURL = () => {
  // Highest priority: explicitly configured at build time
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Development environment
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5000';
  }
  // Production API domain
  return 'https://api.avsbusiness.in';
};

const api = axios.create({
  // Base host (no trailing /api) so our route paths like '/api/...'
  // work in both local and production
  baseURL: getDefaultBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('API Request:', config.method?.toUpperCase(), config.url, config.params || config.data);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.config.url, response.data);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.config?.url, error.response?.data || error.message);

    // Only handle authentication errors for specific endpoints that require authentication
    if (error.response?.status === 401 || error.response?.status === 403) {
      const url = error.config?.url || '';

      // Only redirect to login for authentication-related endpoints
      // Skip redirect for endpoints that might legitimately return 401/403
      const authEndpoints = ['/api/auth/me', '/api/auth/profile', '/api/auth/change-password'];
      const isAuthEndpoint = authEndpoints.some(endpoint => url.includes(endpoint));

      if (isAuthEndpoint) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // Only redirect if we're not already on a login page
        const currentPath = window.location.pathname;
        const isOnLoginPage = currentPath.includes('/login');

        if (!isOnLoginPage) {
          // Redirect admin users to admin login for dashboard access
          const isAdminPath = window.location.pathname.startsWith('/adminavsbusiness.com');
          window.location.href = isAdminPath ? '/adminavsbusiness.com/login' : '/apps.avsbusiness.com/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Public product API functions (for users)
export const fetchProducts = (params) => api.get('/api/ecommerce/products', { params });
export const fetchProduct = (id) => api.get(`/api/ecommerce/products/${id}`);
export const fetchProductCategories = async () => {
  try {
    // Try the ecommerce categories route first
    return await api.get('/api/ecommerce/categories');
  } catch (error) {
    // If that fails, try fallback routes
    console.log('Ecommerce categories route failed, trying fallback routes...');
    try {
      return await api.get('/api/categories');
    } catch (error2) {
      console.log('Categories route also failed');
      throw error2;
    }
  }
};

// Admin product API functions (for admin only)
export const fetchAdminProducts = (params) => api.get('/api/ecommerce/admin/products', { params });
export const fetchAdminProduct = (id) => api.get(`/api/ecommerce/admin/products/${id}`);
export const fetchAdminProductCategories = () => api.get('/api/ecommerce/admin/products/categories');
export const createProduct = (data) => api.post('/api/ecommerce/admin/products', data);
export const updateProduct = (id, data) => api.put(`/api/ecommerce/admin/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/api/ecommerce/admin/products/${id}`);

// Returns API (admin & inventory)
export const createReturn = (data) => api.post('/api/returns', data);
export const fetchReturns = (params) => api.get('/api/returns', { params });
export const deleteReturn = (id) => api.delete(`/api/returns/${id}`);

export default api;

// Ledger API helpers
export const createLedgerEntry = (data) => api.post('/api/accounts-ledger', data);
export const fetchLedgerEntries = (params) => api.get('/api/accounts-ledger', { params });

// Payments API helpers (Razorpay removed)